import React from 'react'
import { motion } from 'framer-motion'
import { useParams, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { 
  ArrowLeft, 
  Smartphone, 
  MapPin, 
  Clock, 
  Calendar, 
  CheckSquare, 
  User, 
  MessageCircle, 
  RefreshCw, 
  Download, 
  Wifi, 
 
  Bell, 
  Play, 
  Pause, 
  Square, 
  FileText, 
  HelpCircle, 
  AlertTriangle, 
  Shield, 
  Zap, 
  Globe, 
  Eye,
  Home,
  LogIn,
  LogOut,
  Settings,
  Camera,
  Navigation,
  Battery,
  Signal
} from 'lucide-react'

export const MobileWorkforceFeatureDetailPage: React.FC = () => {
  const { featureId } = useParams()
  const { t } = useTranslation('workforce')

  if (!featureId || featureId !== 'mobile') {
    return <Navigate to="/workforce" replace />
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900">
      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16"
        >
          <a 
            href="/workforce" 
            className="inline-flex items-center gap-2 text-gray-300 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
          </a>
          
          <div className="text-center">
            <motion.div 
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", bounce: 0.5 }}
              className="inline-flex items-center gap-3 bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-full mb-8 shadow-lg"
            >
              <Smartphone className="w-7 h-7" />
              <span className="font-bold text-lg">{t('features.mobileWorkforce.title')}</span>
            </motion.div>
            <h1 className="text-6xl font-extrabold bg-gradient-to-r from-orange-400 via-red-400 to-pink-400 bg-clip-text text-transparent mb-8">
              {t('mobileDetail.hero.title')}
            </h1>
            <p className="text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              {t('mobileDetail.hero.description')}
            </p>
          </div>
        </motion.div>

        {/* Simulação de Telefone com App */}
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-24"
        >
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-6">
                {t('mobileDetail.hero.title')}
              </h2>
              <p className="text-xl text-gray-300">
                {t('mobileDetail.hero.description')}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Phone Mockup */}
              <div className="relative mx-auto">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 }}
                  className="bg-gray-900 rounded-[3rem] p-4 shadow-2xl border-8 border-gray-800"
                  style={{ width: '320px', height: '640px' }}
                >
                  {/* Phone Status Bar */}
                  <div className="flex justify-between items-center text-white text-xs mb-4 px-2">
                    <div className="flex items-center gap-1">
                      <Signal className="w-3 h-3" />
                      <Wifi className="w-3 h-3" />
                    </div>
                    <div>09:47</div>
                    <div className="flex items-center gap-1">
                      <Battery className="w-3 h-3" />
                      <span>87%</span>
                    </div>
                  </div>

                  {/* App Interface */}
                  <div className="bg-gradient-to-br from-blue-50 to-orange-50 rounded-3xl h-full p-4 overflow-hidden">
                    {/* App Header */}
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                          <Smartphone className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <div className="font-bold text-gray-900">WorkForce</div>
                          <div className="text-xs text-gray-600">{t('mobileDetail.phone.user')}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Bell className="w-5 h-5 text-gray-600" />
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                      </div>
                    </div>

                    {/* Status Card */}
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.8 }}
                      className="bg-white rounded-2xl p-4 mb-4 shadow-lg border border-gray-200"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Clock className="w-5 h-5 text-green-600" />
                          <span className="font-semibold text-gray-900">{t('mobileDetail.phone.activeShift')}</span>
                        </div>
                        <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold">
{t('mobileDetail.phone.working')}
                        </div>
                      </div>
                      <div className="text-2xl font-bold text-gray-900 mb-1">08:47:23</div>
                      <div className="text-sm text-gray-600 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
{t('mobileDetail.phone.location')}
                      </div>
                    </motion.div>

                    {/* Quick Actions */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <motion.button
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 1.0 }}
                        className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white p-3 rounded-xl flex items-center gap-2 text-sm font-medium"
                      >
                        <CheckSquare className="w-4 h-4" />
{t('mobileDetail.phone.tasks')}
                      </motion.button>
                      <motion.button
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 1.1 }}
                        className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-3 rounded-xl flex items-center gap-2 text-sm font-medium"
                      >
                        <MessageCircle className="w-4 h-4" />
{t('mobileDetail.phone.chat')}
                      </motion.button>
                    </div>

                    {/* Current Task */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 1.2 }}
                      className="bg-white rounded-2xl p-4 shadow-lg border border-gray-200"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="font-semibold text-gray-900">{t('mobileDetail.phone.currentTask')}</div>
                        <div className="flex items-center gap-2">
                          <Pause className="w-4 h-4 text-orange-600" />
                          <Square className="w-4 h-4 text-red-600" />
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                        <div className="bg-gradient-to-r from-orange-500 to-red-500 h-2 rounded-full" style={{width: '65%'}}></div>
                      </div>
                      <div className="flex items-center justify-between text-sm text-gray-600">
                        <span>{t('mobileDetail.phone.completed')}</span>
                        <span>{t('mobileDetail.phone.worked')}</span>
                      </div>
                    </motion.div>

                    {/* Bottom Navigation */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 1.4 }}
                      className="absolute bottom-8 left-8 right-8 bg-white/90 backdrop-blur rounded-2xl p-3"
                    >
                      <div className="flex items-center justify-around">
                        <div className="flex flex-col items-center gap-1">
                          <Home className="w-5 h-5 text-orange-600" />
                          <span className="text-xs text-orange-600 font-medium">{t('mobileDetail.phone.home')}</span>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                          <CheckSquare className="w-5 h-5 text-gray-400" />
                          <span className="text-xs text-gray-400">{t('mobileDetail.phone.tasksList')}</span>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                          <MessageCircle className="w-5 h-5 text-gray-400" />
                          <span className="text-xs text-gray-400">{t('mobileDetail.phone.chatTab')}</span>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                          <User className="w-5 h-5 text-gray-400" />
                          <span className="text-xs text-gray-400">{t('mobileDetail.phone.profile')}</span>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </motion.div>

                {/* Floating Elements */}
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.6 }}
                  className="absolute -top-4 -right-4 bg-green-500 text-white px-3 py-2 rounded-full text-sm font-medium shadow-lg"
                >
                  <Download className="w-4 h-4 inline mr-1" />
{t('mobileDetail.phone.installable')}
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.8 }}
                  className="absolute -bottom-4 -left-4 bg-blue-500 text-white px-3 py-2 rounded-full text-sm font-medium shadow-lg"
                >
                  <Zap className="w-4 h-4 inline mr-1" />
{t('mobileDetail.phone.realTime')}
                </motion.div>
              </div>

              {/* Features List */}
              <div className="space-y-6">
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.0 }}
                  className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
                >
                  <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                    <Download className="w-7 h-7 text-orange-400" />
{t('mobileDetail.pwaSection.title')}
                  </h3>
                  <ul className="space-y-3 text-gray-300">
                    <li className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
                        <Smartphone className="w-3 h-3 text-white" />
                      </div>
                      <span>{t('mobileDetail.pwaSection.features.install')}</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                        <Zap className="w-3 h-3 text-white" />
                      </div>
                      <span>{t('mobileDetail.pwaSection.features.performance')}</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                        <Bell className="w-3 h-3 text-white" />
                      </div>
                      <span>{t('mobileDetail.pwaSection.features.notifications')}</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                        <Zap className="w-3 h-3 text-white" />
                      </div>
                      <span>{t('mobileDetail.pwaSection.features.sync')}</span>
                    </li>
                  </ul>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.2 }}
                  className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
                >
                  <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                    <Shield className="w-7 h-7 text-green-400" />
{t('mobileDetail.authSection.title')}
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-white mb-1">OAuth2</div>
                      <div className="text-sm text-green-300">{t('mobileDetail.authSection.googleLogin')}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-white mb-1">Local</div>
                      <div className="text-sm text-blue-300">{t('mobileDetail.authSection.emailPassword')}</div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Ponto Digital */}
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mb-24"
        >
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-3xl p-10 shadow-2xl text-white">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur px-6 py-3 rounded-full mb-6">
                <MapPin className="w-6 h-6" />
                <span className="font-semibold">{t('mobileDetail.timeClocking.title')}</span>
              </div>
              <h2 className="text-5xl font-bold mb-6">
                {t('mobileDetail.timeClocking.subtitle')}
              </h2>
              <p className="text-xl text-green-100 max-w-3xl mx-auto">
                {t('mobileDetail.timeClocking.description')}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <motion.div 
                whileHover={{ y: -10 }}
                className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
              >
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <LogIn className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-3">{t('mobileDetail.timeClocking.clockIn.title')}</h3>
                <ul className="space-y-2 text-green-100 text-sm">
                  {(t('mobileDetail.timeClocking.clockIn.features', { returnObjects: true }) as string[]).map((feature: string, i: number) => (
                    <li key={i}>• {feature}</li>
                  ))}
                </ul>
              </motion.div>

              <motion.div 
                whileHover={{ y: -10 }}
                className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
              >
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <Clock className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-3">{t('mobileDetail.timeClocking.shiftControl.title')}</h3>
                <ul className="space-y-2 text-green-100 text-sm">
                  {(t('mobileDetail.timeClocking.shiftControl.features', { returnObjects: true }) as string[]).map((feature: string, i: number) => (
                    <li key={i}>• {feature}</li>
                  ))}
                </ul>
              </motion.div>

              <motion.div 
                whileHover={{ y: -10 }}
                className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
              >
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <LogOut className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-3">{t('mobileDetail.timeClocking.clockOut.title')}</h3>
                <ul className="space-y-2 text-green-100 text-sm">
                  {(t('mobileDetail.timeClocking.clockOut.features', { returnObjects: true }) as string[]).map((feature: string, i: number) => (
                    <li key={i}>• {feature}</li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* Gestão de Tarefas */}
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="mb-24"
        >
          <div className="bg-white rounded-3xl p-10 shadow-2xl">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-3 bg-blue-100 text-blue-800 px-6 py-3 rounded-full mb-6">
                <CheckSquare className="w-6 h-6" />
                <span className="font-semibold">{t('mobileDetail.taskManagement.title')}</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
{t('mobileDetail.taskManagement.subtitle')}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
{t('mobileDetail.taskManagement.description')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Tarefas do Turno */}
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 border border-blue-200"
              >
                <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mb-4">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{t('mobileDetail.taskManagement.shiftTasks.title')}</h3>
                <p className="text-gray-600 mb-4">{t('mobileDetail.taskManagement.shiftTasks.description')}</p>
                <div className="space-y-2">
                  <div className="bg-white rounded-lg p-3 border-l-4 border-blue-500">
                    <div className="font-medium text-sm">{t('mobileDetail.taskManagement.shiftTasks.examples.checkin')}</div>
                    <div className="text-xs text-gray-500">{t('mobileDetail.taskManagement.shiftTasks.examples.priority')}</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border-l-4 border-green-500">
                    <div className="font-medium text-sm">{t('mobileDetail.taskManagement.shiftTasks.examples.cleaning')}</div>
                    <div className="text-xs text-gray-500">{t('mobileDetail.taskManagement.shiftTasks.examples.inProgress')}</div>
                  </div>
                </div>
              </motion.div>

              {/* Tarefas Pessoais */}
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200"
              >
                <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center mb-4">
                  <User className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{t('mobileDetail.taskManagement.personalTasks.title')}</h3>
                <p className="text-gray-600 mb-4">{t('mobileDetail.taskManagement.personalTasks.description')}</p>
                <div className="space-y-2">
                  <div className="bg-white rounded-lg p-3 border-l-4 border-purple-500">
                    <div className="font-medium text-sm">{t('mobileDetail.taskManagement.personalTasks.examples.training')}</div>
                    <div className="text-xs text-gray-500">{t('mobileDetail.taskManagement.personalTasks.examples.deadline')}</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border-l-4 border-orange-500">
                    <div className="font-medium text-sm">{t('mobileDetail.taskManagement.personalTasks.examples.evaluation')}</div>
                    <div className="text-xs text-gray-500">{t('mobileDetail.taskManagement.personalTasks.examples.urgent')}</div>
                  </div>
                </div>
              </motion.div>

              {/* Controles de Tarefa */}
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-6 border border-orange-200"
              >
                <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center mb-4">
                  <Settings className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{t('mobileDetail.taskManagement.controls.title')}</h3>
                <p className="text-gray-600 mb-4">{t('mobileDetail.taskManagement.controls.description')}</p>
                <div className="grid grid-cols-2 gap-2">
                  <button className="bg-green-100 text-green-700 p-2 rounded-lg text-xs font-medium flex items-center gap-1">
                    <Play className="w-3 h-3" />
                    {t('mobileDetail.taskManagement.controls.start')}
                  </button>
                  <button className="bg-orange-100 text-orange-700 p-2 rounded-lg text-xs font-medium flex items-center gap-1">
                    <Pause className="w-3 h-3" />
                    {t('mobileDetail.taskManagement.controls.pause')}
                  </button>
                  <button className="bg-blue-100 text-blue-700 p-2 rounded-lg text-xs font-medium flex items-center gap-1">
                    <Eye className="w-3 h-3" />
                    {t('mobileDetail.taskManagement.controls.details')}
                  </button>
                  <button className="bg-red-100 text-red-700 p-2 rounded-lg text-xs font-medium flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {t('mobileDetail.taskManagement.controls.block')}
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* Recursos de Apoio */}
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          className="mb-24"
        >
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-3xl p-10 shadow-2xl text-white">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur px-6 py-3 rounded-full mb-6">
                <HelpCircle className="w-6 h-6" />
                <span className="font-semibold">{t('mobileDetail.support.title')}</span>
              </div>
              <h2 className="text-5xl font-bold mb-6">
                {t('mobileDetail.support.subtitle')}
              </h2>
              <p className="text-xl text-indigo-100 max-w-3xl mx-auto">
                {t('mobileDetail.support.description')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <motion.div 
                whileHover={{ scale: 1.02 }}
                className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
              >
                <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
                  <FileText className="w-7 h-7" />
                  {t('mobileDetail.support.documentation.title')}
                </h3>
                <div className="space-y-4">
                  <div className="bg-white/10 rounded-xl p-4">
                    <div className="font-semibold mb-2">{t('mobileDetail.support.documentation.procedures')}</div>
                    <div className="text-sm text-indigo-100">{t('mobileDetail.support.documentation.proceduresDesc')}</div>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4">
                    <div className="font-semibold mb-2">{t('mobileDetail.support.documentation.instructions')}</div>
                    <div className="text-sm text-indigo-100">{t('mobileDetail.support.documentation.instructionsDesc')}</div>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4">
                    <div className="font-semibold mb-2">{t('mobileDetail.support.documentation.evidence')}</div>
                    <div className="text-sm text-indigo-100">{t('mobileDetail.support.documentation.evidenceDesc')}</div>
                  </div>
                </div>
              </motion.div>

              <motion.div 
                whileHover={{ scale: 1.02 }}
                className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
              >
                <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
                  <MessageCircle className="w-7 h-7" />
                  {t('mobileDetail.support.communication.title')}
                </h3>
                <div className="space-y-4">
                  <div className="bg-white/10 rounded-xl p-4">
                    <div className="font-semibold mb-2">{t('mobileDetail.support.communication.help')}</div>
                    <div className="text-sm text-indigo-100">{t('mobileDetail.support.communication.helpDesc')}</div>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4">
                    <div className="font-semibold mb-2">{t('mobileDetail.support.communication.block')}</div>
                    <div className="text-sm text-indigo-100">{t('mobileDetail.support.communication.blockDesc')}</div>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4">
                    <div className="font-semibold mb-2">{t('mobileDetail.support.communication.chat')}</div>
                    <div className="text-sm text-indigo-100">{t('mobileDetail.support.communication.chatDesc')}</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* Sistema de Solicitações */}
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5 }}
          className="mb-24"
        >
          <div className="bg-white rounded-3xl p-10 shadow-2xl">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-3 bg-amber-100 text-amber-800 px-6 py-3 rounded-full mb-6">
                <RefreshCw className="w-6 h-6" />
                <span className="font-semibold">{t('mobileDetail.requests.title')}</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                {t('mobileDetail.requests.subtitle')}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('mobileDetail.requests.description')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <motion.div 
                whileHover={{ y: -5 }}
                className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200 text-center"
              >
                <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <RefreshCw className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{t('mobileDetail.requests.shiftExchange.title')}</h3>
                <p className="text-sm text-gray-600 mb-3">{t('mobileDetail.requests.shiftExchange.description')}</p>
                <div className="bg-white rounded-lg p-2">
                  <div className="text-xs text-blue-700 font-medium">{t('mobileDetail.requests.shiftExchange.pending')}</div>
                </div>
              </motion.div>

              <motion.div 
                whileHover={{ y: -5 }}
                className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-6 border border-red-200 text-center"
              >
                <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{t('mobileDetail.requests.absences.title')}</h3>
                <p className="text-sm text-gray-600 mb-3">{t('mobileDetail.requests.absences.description')}</p>
                <div className="bg-white rounded-lg p-2">
                  <div className="text-xs text-red-700 font-medium">{t('mobileDetail.requests.absences.approval')}</div>
                </div>
              </motion.div>

              <motion.div 
                whileHover={{ y: -5 }}
                className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 border border-green-200 text-center"
              >
                <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{t('mobileDetail.requests.vacation.title')}</h3>
                <p className="text-sm text-gray-600 mb-3">{t('mobileDetail.requests.vacation.description')}</p>
                <div className="bg-white rounded-lg p-2">
                  <div className="text-xs text-green-700 font-medium">{t('mobileDetail.requests.vacation.workflow')}</div>
                </div>
              </motion.div>

              <motion.div 
                whileHover={{ y: -5 }}
                className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-6 border border-purple-200 text-center"
              >
                <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{t('mobileDetail.requests.communication.title')}</h3>
                <p className="text-sm text-gray-600 mb-3">{t('mobileDetail.requests.communication.description')}</p>
                <div className="bg-white rounded-lg p-2">
                  <div className="text-xs text-purple-700 font-medium">{t('mobileDetail.requests.communication.response')}</div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* Interface Mobile Demonstração */}
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.8 }}
          className="mb-24"
        >
          <div className="bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800 rounded-3xl p-10 shadow-2xl text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
            
            <div className="relative z-10">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur px-6 py-3 rounded-full mb-6">
                  <Smartphone className="w-6 h-6" />
                  <span className="font-semibold">{t('mobileDetail.mobileInterface.title')}</span>
                </div>
                <h2 className="text-4xl font-bold mb-6">
                  {t('mobileDetail.mobileInterface.subtitle')}
                </h2>
                <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                  {t('mobileDetail.mobileInterface.description')}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
                >
                  <Camera className="w-8 h-8 mb-4 text-orange-400" />
                  <h3 className="text-lg font-bold mb-3">{t('mobileDetail.mobileInterface.touchOptimized.title')}</h3>
                  <ul className="space-y-1 text-sm text-gray-300">
                    {(t('mobileDetail.mobileInterface.touchOptimized.features', { returnObjects: true }) as string[]).map((feature: string, i: number) => (
                      <li key={i}>• {feature}</li>
                    ))}
                  </ul>
                </motion.div>

                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
                >
                  <Navigation className="w-8 h-8 mb-4 text-green-400" />
                  <h3 className="text-lg font-bold mb-3">{t('mobileDetail.mobileInterface.gpsIntegrated.title')}</h3>
                  <ul className="space-y-1 text-sm text-gray-300">
                    {(t('mobileDetail.mobileInterface.gpsIntegrated.features', { returnObjects: true }) as string[]).map((feature: string, i: number) => (
                      <li key={i}>• {feature}</li>
                    ))}
                  </ul>
                </motion.div>

                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
                >
                  <Wifi className="w-8 h-8 mb-4 text-blue-400" />
                  <h3 className="text-lg font-bold mb-3">{t('mobileDetail.mobileInterface.connectivity.title')}</h3>
                  <ul className="space-y-1 text-sm text-gray-300">
                    {(t('mobileDetail.mobileInterface.connectivity.features', { returnObjects: true }) as string[]).map((feature: string, i: number) => (
                      <li key={i}>• {feature}</li>
                    ))}
                  </ul>
                </motion.div>

                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
                >
                  <Bell className="w-8 h-8 mb-4 text-pink-400" />
                  <h3 className="text-lg font-bold mb-3">{t('mobileDetail.mobileInterface.notifications.title')}</h3>
                  <ul className="space-y-1 text-sm text-gray-300">
                    {(t('mobileDetail.mobileInterface.notifications.features', { returnObjects: true }) as string[]).map((feature: string, i: number) => (
                      <li key={i}>• {feature}</li>
                    ))}
                  </ul>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Fluxo de Trabalho */}
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.1 }}
          className="mb-24"
        >
          <div className="bg-white rounded-3xl p-10 shadow-2xl">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-3 bg-teal-100 text-teal-800 px-6 py-3 rounded-full mb-6">
                <Zap className="w-6 h-6" />
                <span className="font-semibold">{t('mobileDetail.workflow.title')}</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                {t('mobileDetail.workflow.title')}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('mobileDetail.workflow.description')}
              </p>
            </div>

            <div className="relative">
              {/* Timeline */}
              <div className="absolute left-8 top-8 bottom-8 w-0.5 bg-gradient-to-b from-orange-500 to-red-500"></div>
              
              <div className="space-y-8">
                {[
                  {
                    icon: LogIn,
                    title: t('mobileDetail.workflow.steps.clockIn.title'),
                    description: t('mobileDetail.workflow.steps.clockIn.description'),
                    color: "green",
                    details: t('mobileDetail.workflow.steps.clockIn.details', { returnObjects: true })
                  },
                  {
                    icon: CheckSquare,
                    title: t('mobileDetail.workflow.steps.loadTasks.title'),
                    description: t('mobileDetail.workflow.steps.loadTasks.description'),
                    color: "blue",
                    details: t('mobileDetail.workflow.steps.loadTasks.details', { returnObjects: true })
                  },
                  {
                    icon: Play,
                    title: t('mobileDetail.workflow.steps.executeWork.title'),
                    description: t('mobileDetail.workflow.steps.executeWork.description'),
                    color: "purple",
                    details: t('mobileDetail.workflow.steps.executeWork.details', { returnObjects: true })
                  },
                  {
                    icon: MessageCircle,
                    title: t('mobileDetail.workflow.steps.communicateStatus.title'),
                    description: t('mobileDetail.workflow.steps.communicateStatus.description'),
                    color: "indigo",
                    details: t('mobileDetail.workflow.steps.communicateStatus.details', { returnObjects: true })
                  },
                  {
                    icon: LogOut,
                    title: t('mobileDetail.workflow.steps.clockOut.title'),
                    description: t('mobileDetail.workflow.steps.clockOut.description'),
                    color: "red",
                    details: t('mobileDetail.workflow.steps.clockOut.details', { returnObjects: true })
                  }
                ].map((step, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 2.3 + index * 0.2 }}
                    className="relative flex items-start gap-6"
                  >
                    <div className={`w-16 h-16 bg-gradient-to-r from-${step.color}-500 to-${step.color}-600 rounded-2xl flex items-center justify-center shadow-lg relative z-10`}>
                      <step.icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                      <p className="text-gray-600 mb-3">{step.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {(step.details as string[]).map((detail: string, i: number) => (
                          <span key={i} className={`bg-${step.color}-100 text-${step.color}-700 px-3 py-1 rounded-full text-xs font-medium`}>
                            {detail}
                          </span>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.section>

        {/* Especificações Técnicas */}
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.4 }}
          className="mb-24"
        >
          <div className="bg-gradient-to-br from-slate-800 via-gray-800 to-zinc-800 rounded-3xl p-10 shadow-2xl text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-orange-500/10 to-red-500/10"></div>
            
            <div className="relative z-10">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur px-6 py-3 rounded-full mb-6">
                  <Globe className="w-6 h-6" />
                  <span className="font-semibold">{t('mobileDetail.technicalSpecs.title')}</span>
                </div>
                <h2 className="text-4xl font-bold mb-6">
                  {t('mobileDetail.technicalSpecs.subtitle')}
                </h2>
                <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                  {t('mobileDetail.technicalSpecs.description')}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
                >
                  <Smartphone className="w-8 h-8 mb-4 text-orange-400" />
                  <h3 className="text-lg font-bold mb-3">{t('mobileDetail.technicalSpecs.advancedPwa.title')}</h3>
                  <ul className="space-y-1 text-sm text-gray-300">
                    {(t('mobileDetail.technicalSpecs.advancedPwa.features', { returnObjects: true }) as string[]).map((feature: string, i: number) => (
                      <li key={i}>• {feature}</li>
                    ))}
                  </ul>
                </motion.div>

                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
                >
                  <Globe className="w-8 h-8 mb-4 text-blue-400" />
                  <h3 className="text-lg font-bold mb-3">{t('mobileDetail.technicalSpecs.websocket.title')}</h3>
                  <ul className="space-y-1 text-sm text-gray-300">
                    {(t('mobileDetail.technicalSpecs.websocket.features', { returnObjects: true }) as string[]).map((feature: string, i: number) => (
                      <li key={i}>• {feature}</li>
                    ))}
                  </ul>
                </motion.div>

                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
                >
                  <Shield className="w-8 h-8 mb-4 text-green-400" />
                  <h3 className="text-lg font-bold mb-3">{t('mobileDetail.technicalSpecs.mobileSecurity.title')}</h3>
                  <ul className="space-y-1 text-sm text-gray-300">
                    {(t('mobileDetail.technicalSpecs.mobileSecurity.features', { returnObjects: true }) as string[]).map((feature: string, i: number) => (
                      <li key={i}>• {feature}</li>
                    ))}
                  </ul>
                </motion.div>

                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
                >
                  <MapPin className="w-8 h-8 mb-4 text-red-400" />
                  <h3 className="text-lg font-bold mb-3">{t('mobileDetail.technicalSpecs.geolocation.title')}</h3>
                  <ul className="space-y-1 text-sm text-gray-300">
                    {(t('mobileDetail.technicalSpecs.geolocation.features', { returnObjects: true }) as string[]).map((feature: string, i: number) => (
                      <li key={i}>• {feature}</li>
                    ))}
                  </ul>
                </motion.div>

                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
                >
                  <Zap className="w-8 h-8 mb-4 text-yellow-400" />
                  <h3 className="text-lg font-bold mb-3">{t('mobileDetail.technicalSpecs.performance.title')}</h3>
                  <ul className="space-y-1 text-sm text-gray-300">
                    {(t('mobileDetail.technicalSpecs.performance.features', { returnObjects: true }) as string[]).map((feature: string, i: number) => (
                      <li key={i}>• {feature}</li>
                    ))}
                  </ul>
                </motion.div>

                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
                >
                  <RefreshCw className="w-8 h-8 mb-4 text-purple-400" />
                  <h3 className="text-lg font-bold mb-3">{t('mobileDetail.technicalSpecs.globalState.title')}</h3>
                  <ul className="space-y-1 text-sm text-gray-300">
                    {(t('mobileDetail.technicalSpecs.globalState.features', { returnObjects: true }) as string[]).map((feature: string, i: number) => (
                      <li key={i}>• {feature}</li>
                    ))}
                  </ul>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Call to Action */}
        <motion.section
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 2.6 }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-orange-600 via-red-600 to-pink-600 rounded-3xl p-12 shadow-2xl text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-black/30"></div>
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
            
            <div className="relative z-10">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2.8 }}
              >
                <h2 className="text-5xl font-bold mb-8">
                  {t('mobileDetail.cta.title')}
                </h2>
                <p className="text-2xl mb-10 max-w-3xl mx-auto text-orange-100">
                  {t('mobileDetail.cta.description')}
                </p>
                

                <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
                  <div>
                    <div className="text-3xl font-bold mb-2">&lt; 1MB</div>
                    <div className="text-orange-200">{t('mobileDetail.cta.stats.appSize')}</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold mb-2">&lt; 3s</div>
                    <div className="text-orange-200">{t('mobileDetail.cta.stats.loadTime')}</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold mb-2">100%</div>
                    <div className="text-orange-200">{t('mobileDetail.cta.stats.synchronized')}</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold mb-2">24/7</div>
                    <div className="text-orange-200">{t('mobileDetail.cta.stats.availability')}</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.section>
      </div>
    </div>
  )
}

export default MobileWorkforceFeatureDetailPage